import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  individualClientFiles: [],
  currentIndividualClientFile: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all Individual Client Files
export const fetchIndividualClientFiles = createAsyncThunk(
  'individualClientFile/fetchIndividualClientFiles',
  async (patientId, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/individual-client-files/${patientId}`
        : '/individual-client-files';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch Individual Client Files'
      );
    }
  }
);

// Fetch a single Individual Client File
export const fetchIndividualClientFile = createAsyncThunk(
  'individualClientFile/fetchIndividualClientFile',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/individual-client-files/show/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch Individual Client File'
      );
    }
  }
);

// Create a new Individual Client File
export const createIndividualClientFile = createAsyncThunk(
  'individualClientFile/createIndividualClientFile',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post('/individual-client-files', formData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create Individual Client File'
      );
    }
  }
);

// Update Individual Client File
export const updateIndividualClientFile = createAsyncThunk(
  'individualClientFile/updateIndividualClientFile',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/individual-client-files/${id}`, formData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update Individual Client File'
      );
    }
  }
);

// Delete Individual Client File
export const deleteIndividualClientFile = createAsyncThunk(
  'individualClientFile/deleteIndividualClientFile',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/individual-client-files/${id}`);
      return { id, message: response.data.message };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete Individual Client File'
      );
    }
  }
);

// ==================== Slice ====================
const individualClientFileSlice = createSlice({
  name: 'individualClientFile',
  initialState,
  reducers: {
    clearCurrentIndividualClientFile: (state) => {
      state.currentIndividualClientFile = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch List
      .addCase(fetchIndividualClientFiles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchIndividualClientFiles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.individualClientFiles = action.payload;
      })
      .addCase(fetchIndividualClientFiles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch Single
      .addCase(fetchIndividualClientFile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchIndividualClientFile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentIndividualClientFile = action.payload;
      })
      .addCase(fetchIndividualClientFile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create
      .addCase(createIndividualClientFile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createIndividualClientFile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.individualClientFiles.unshift(action.payload);
      })
      .addCase(createIndividualClientFile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update
      .addCase(updateIndividualClientFile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateIndividualClientFile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentIndividualClientFile = action.payload;
        const index = state.individualClientFiles.findIndex(
          (r) => r.id === action.payload.id
        );
        if (index !== -1) {
          state.individualClientFiles[index] = action.payload;
        }
      })
      .addCase(updateIndividualClientFile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete
      .addCase(deleteIndividualClientFile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteIndividualClientFile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.individualClientFiles = state.individualClientFiles.filter(
          (r) => r.id !== action.payload.id
        );
      })
      .addCase(deleteIndividualClientFile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCurrentIndividualClientFile, clearError } =
  individualClientFileSlice.actions;
export default individualClientFileSlice.reducer;
